import { formatDateTimeInTz } from "@/utils/datetime";

const UNITS: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
  { unit: "year", seconds: 60 * 60 * 24 * 365 },
  { unit: "month", seconds: 60 * 60 * 24 * 30 },
  { unit: "week", seconds: 60 * 60 * 24 * 7 },
  { unit: "day", seconds: 60 * 60 * 24 },
  { unit: "hour", seconds: 60 * 60 },
  { unit: "minute", seconds: 60 },
  { unit: "second", seconds: 1 },
];

const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

/**
 * Relative-time labels ("3 minutes ago", "in 2 days") are tz-agnostic —
 * the delta between two instants is identical in every timezone — so
 * this helper still takes no `tz` argument.
 */
export function relativeTime(iso: string | Date, now: Date = new Date()): string {
  const target = typeof iso === "string" ? new Date(iso) : iso;
  const diffSec = Math.round((target.getTime() - now.getTime()) / 1000);
  if (Number.isNaN(diffSec)) return "";
  const abs = Math.abs(diffSec);
  for (const { unit, seconds } of UNITS) {
    if (abs >= seconds || unit === "second") {
      const value = Math.round(diffSec / seconds);
      return rtf.format(value, unit);
    }
  }
  return "";
}

/**
 * A YEARLESS absolute date-time (`Sep 4, 10:00 AM`) in the VIEWER's zone.
 *
 * 🔴 `tz` IS REQUIRED. It was `tz?: string | null` under an `@deprecated` tag
 * telling callers to pass a zone — and all five call sites ignored it, because
 * an optional parameter carrying an instruction is a suggestion, not a
 * contract. Every one rendered in `America/Phoenix` for every viewer. The tag
 * is gone because the hazard it warned about is now structurally impossible
 * rather than merely discouraged.
 *
 * 🔴 DO NOT "simplify" this into `useDateFormatter().dateTime(iso)`. The opts
 * below pin `year: undefined` — this helper exists precisely to render WITHOUT
 * the year, and the default `dateTime` formatter includes it. Collapsing them
 * silently changes the text at all five sites.
 *
 * Pass `useDateFormatter().tz` from a component. `string | null` (not
 * `| undefined`) is deliberate: null is a real "the user set no preference"
 * value the formatter folds to the fallback, but a caller cannot satisfy it by
 * omitting the argument.
 */
export function formatDateTime(iso: string | Date, tz: string | null): string {
  return formatDateTimeInTz(iso, tz, {
    year: undefined,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
